import { useRef, useState } from 'react';
import ConfirmDialog from '../ui/ConfirmDialog';
import DocumentPreviewModal from '../ui/DocumentPreviewModal';
import type { UploadCaseFileResponse } from '../../types/criminalApi';
import type { ApiResult } from '../../types';
import type { CriminalDocumentRow } from '../../types/criminalCaseRequest';
import {
  CASE_DOC_ACCEPT,
  CASE_DOC_DELETED_SUCCESS,
  validateCaseDocumentFile,
} from '../../utils/caseDocumentUpload';
import {
  clearCriminalPreviewFile,
  getCriminalPreviewFile,
  setCriminalPreviewFile,
} from '../../utils/criminalDocumentPreviewCache';
import { formatUploadTimeLabel } from '../../utils/dateFormat';
import { displayClientFileName, normalizeDocumentFileFields } from '../../utils/fileDisplayName';
import { setFlashToast } from '../../utils/flashToast';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="ccr-err" style={{ marginTop: 4 }} role="alert">
      {message}
    </div>
  );
}

export type WizardTempUploadedFile = {
  id: string;
  fileName: string;
  originalFileName: string;
  uploadTime: string;
};

type Props = {
  files: WizardTempUploadedFile[];
  onChange: (files: WizardTempUploadedFile[]) => void;
  uploadFile: (file: File) => Promise<ApiResult<UploadCaseFileResponse>>;
  error?: string;
  onClearError?: () => void;
  addButtonLabel?: string;
  /** Place add button on same row as sibling controls (e.g. radio group). File list stays below. */
  addButtonInline?: boolean;
};

function CloudUploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function FileDocIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function toPreviewDoc(file: WizardTempUploadedFile): CriminalDocumentRow {
  return {
    id: file.id,
    checklistItemId: '',
    documentTypeId: '',
    documentName: file.originalFileName || file.fileName,
    mandatory: false,
    fileName: file.fileName,
    originalFileName: file.originalFileName,
    uploadTime: file.uploadTime,
    skipReason: '',
  };
}

export function WizardTempFileUploadList({
  files,
  onChange,
  uploadFile,
  error,
  onClearError,
  addButtonLabel = 'Upload Documents',
  addButtonInline = false,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<WizardTempUploadedFile | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const openFilePicker = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const validation = validateCaseDocumentFile(file);
    if (!validation.ok) {
      setFlashToast({ message: validation.message, variant: 'error' });
      return;
    }

    const id = crypto.randomUUID();
    setUploading(true);
    setCriminalPreviewFile(id, file);

    const r = await uploadFile(file);
    setUploading(false);

    if (!r.success) {
      clearCriminalPreviewFile(id);
      setFlashToast({ message: r.message ?? 'File upload failed.', variant: 'error' });
      return;
    }

    const { fileName: storedKey, originalFileName: displayName } = normalizeDocumentFileFields(
      r.data.file_name ?? '',
      r.data.original_file_name ?? file.name,
    );

    onChange([
      ...files,
      {
        id,
        fileName: storedKey,
        originalFileName: displayName || file.name,
        uploadTime: r.data.upload_time ?? new Date().toISOString(),
      },
    ]);
    onClearError?.();
  };

  const requestRemove = (fileId: string) => {
    const row = files.find((f) => f.id === fileId);
    if (!row?.fileName && !getCriminalPreviewFile(fileId)) return;
    setPendingDeleteId(fileId);
    setDeleteConfirmOpen(true);
  };

  const confirmRemove = () => {
    const fileId = pendingDeleteId;
    if (!fileId) {
      setDeleteConfirmOpen(false);
      return;
    }
    clearCriminalPreviewFile(fileId);
    onChange(files.filter((f) => f.id !== fileId));
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
    if (previewFile?.id === fileId) setPreviewFile(null);
    setFlashToast({ message: CASE_DOC_DELETED_SUCCESS, variant: 'success' });
  };

  const fileStack =
    files.length > 0 ? (
      <div className="ccr-temp-file-stack">
        {files.map((file) => {
          const displayName = displayClientFileName(file.originalFileName, file.fileName);
          const uploadTime = formatUploadTimeLabel(file.uploadTime);
          return (
            <div key={file.id} className="ccr-doc-upload-file">
              <div className="ccr-doc-upload-file-ico">
                <FileDocIcon />
              </div>
              <div className="ccr-doc-upload-file-body">
                <div className="ccr-doc-upload-file-name" title={displayName}>
                  {displayName}
                </div>
                {uploadTime ? <div className="ccr-doc-upload-file-meta">{uploadTime}</div> : null}
              </div>
              <div className="ccr-doc-upload-file-acts">
                <button
                  type="button"
                  className="act-b"
                  aria-label={`Preview ${displayName}`}
                  data-tip="Preview"
                  onClick={() => setPreviewFile(file)}
                >
                  <EyeIcon />
                </button>
                <button
                  type="button"
                  className="act-b del"
                  aria-label={`Remove ${displayName}`}
                  data-tip="Delete"
                  onClick={() => requestRemove(file.id)}
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    ) : null;

  const addButton = (
    <button
      type="button"
      className="ccr-doc-upload-btn"
      style={{ marginTop: addButtonInline ? 0 : files.length > 0 ? 10 : 0 }}
      disabled={uploading}
      onClick={openFilePicker}
    >
      <CloudUploadIcon />
      {uploading ? 'Uploading…' : addButtonLabel}
    </button>
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={CASE_DOC_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => void onFileSelected(e)}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Document"
        message="Are you sure you want to delete? This action cannot be undone."
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={confirmRemove}
      />

      {addButtonInline ? (
        <div className="ccr-temp-file-upload-zone is-inline-btn">{addButton}</div>
      ) : (
        <div className="ccr-temp-file-upload-zone">
          {fileStack}
          {addButton}
        </div>
      )}

      {addButtonInline && fileStack ? (
        <div className="ccr-temp-file-upload-zone">{fileStack}</div>
      ) : null}

      <FieldError message={error} />

      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        doc={previewFile ? toPreviewDoc(previewFile) : null}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
}
